import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import {
  VENUE_COLORS,
  getVenueIdFromDiagonalClick,
  getLocationDisplayName,
  VENUE_WEDGE_CLIP_PATHS,
  VENUE_WEDGE_TRANSFORM_ORIGINS,
  VENUE_HOVER_SCALE
} from '../utils/venueCalendarUtils';

const cellBaseSx = {
  height: { xs: 32, sm: 28, md: 30 },
  border: '1px solid',
  borderColor: 'divider',
  position: 'relative',
  overflow: 'hidden'
};

const VenueDiagonalSlotCell = ({
  cell,
  isClickable,
  isHourEnd,
  isLastSlot,
  onClick
}) => {
  const [hoveredVenueId, setHoveredVenueId] = useState(null);
  const venueIds = cell.bookableVenues || [];
  const multiVenue = isClickable && venueIds.length > 1;
  const marginSx = { mb: isHourEnd && !isLastSlot ? 0.25 : 0 };

  const handleMouseMove = (event) => {
    if (!multiVenue) {
      setHoveredVenueId(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const relX = (event.clientX - rect.left) / rect.width;
    const relY = (event.clientY - rect.top) / rect.height;
    setHoveredVenueId(getVenueIdFromDiagonalClick(venueIds, relX, relY));
  };

  if (cell.type === 'user_reservation') {
    return (
      <Box
        sx={{
          ...cellBaseSx,
          ...marginSx,
          backgroundColor: cell.background,
          border: '4px solid',
          borderColor: '#d32f2f',
          cursor: 'default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '0.5rem', sm: '0.7rem' },
            color: 'rgba(0,0,0,0.82)',
            fontWeight: 600,
            textAlign: 'center',
            lineHeight: 1,
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title="Jūsų rezervacija"
        >
          Jūsų rezervacija
        </Typography>
      </Box>
    );
  }

  const handleClick = (event) => {
    if (!isClickable || !onClick) return;
    if (multiVenue) {
      const rect = event.currentTarget.getBoundingClientRect();
      const relX = (event.clientX - rect.left) / rect.width;
      const relY = (event.clientY - rect.top) / rect.height;
      onClick(event, getVenueIdFromDiagonalClick(venueIds, relX, relY));
      return;
    }
    onClick(event, venueIds[0] || null);
  };

  if (!multiVenue) {
    return (
      <Box
        onClick={isClickable ? handleClick : undefined}
        sx={{
          ...cellBaseSx,
          ...marginSx,
          background: cell.background,
          backgroundColor: cell.backgroundColor || 'grey.300',
          cursor: isClickable ? 'pointer' : 'default',
          transition: 'filter 0.15s ease',
          ...(isClickable && {
            '&:hover': { filter: 'brightness(1.08)' }
          })
        }}
      />
    );
  }

  const clipPaths = VENUE_WEDGE_CLIP_PATHS[venueIds.length] || VENUE_WEDGE_CLIP_PATHS[2];
  const origins = VENUE_WEDGE_TRANSFORM_ORIGINS[venueIds.length] || VENUE_WEDGE_TRANSFORM_ORIGINS[2];

  return (
    <Box
      onClick={isClickable ? handleClick : undefined}
      onMouseMove={handleMouseMove}
      onMouseLeave={() => setHoveredVenueId(null)}
      sx={{
        ...cellBaseSx,
        ...marginSx,
        cursor: 'pointer',
        overflow: hoveredVenueId ? 'visible' : 'hidden',
        zIndex: hoveredVenueId ? 10 : 1
      }}
    >
      {venueIds.map((venueId, index) => {
        const isHovered = hoveredVenueId === venueId;
        return (
          <Box
            key={venueId}
            sx={{
              position: 'absolute',
              inset: 0,
              clipPath: clipPaths[index],
              backgroundColor: VENUE_COLORS[venueId],
              transform: isHovered ? `scale(${VENUE_HOVER_SCALE})` : 'scale(1)',
              transformOrigin: origins[index],
              transition: 'transform 0.14s ease, filter 0.14s ease, box-shadow 0.14s ease',
              filter: isHovered ? 'brightness(1.08)' : 'none',
              boxShadow: isHovered ? 'inset 0 0 0 1.5px rgba(255,255,255,0.9)' : 'none',
              zIndex: isHovered ? 3 : index + 1,
              pointerEvents: 'none'
            }}
          />
        );
      })}
      {hoveredVenueId && (
        <Typography
          variant="caption"
          sx={{
            position: 'absolute',
            bottom: 1,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 4,
            fontSize: { xs: '0.42rem', sm: '0.55rem' },
            fontWeight: 700,
            color: 'white',
            textShadow: '0 1px 2px rgba(0,0,0,0.85)',
            pointerEvents: 'none',
            lineHeight: 1,
            whiteSpace: 'nowrap'
          }}
        >
          {getLocationDisplayName(hoveredVenueId)}
        </Typography>
      )}
    </Box>
  );
};

export default VenueDiagonalSlotCell;
